import { useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import { parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { setReviewCreationKey, useReviewCreationV1Data } from 'reducers/reviewCreationV1Slice';

import { ArrowRight } from 'assets/assetsV1/iconComponents';

import { formattedDateRangeShort, isEmptyDateRangeValues } from 'uiToolkitV1/DateRange/DateRangeSelectDashed';
import { ReviewCreationTimeline } from 'uiToolkitV1/ReviewCreationTimeline';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import {
  ALL_SECTIONS_IN_ORDER,
  REVIEW_RESPONSE_PROVIDER_TYPE_WITH_REMINDER,
} from '../../configurePanels/TimelinesRemindersPanel';

export const SingleTypeReminders = ({ singleTypeReminderData, handleOpenTimelinePanel }) => {
  const reminderCount = singleTypeReminderData.length;

  return (
    <Flex alignItems="center" cursor="pointer" onClick={handleOpenTimelinePanel}>
      <TextLabel color="text.g2">Reminders: </TextLabel>

      <TextLabel bold ml={0.5}>
        {reminderCount === 0 ? 'OFF' : reminderCount}
      </TextLabel>

      <ArrowRight boxSize="24px" color="button.blue.default" />
    </Flex>
  );
};

const SingleTimelineReminder = ({ singleTimelineData, singleTypeReminderData = [] }) => {
  const { reviewStage, startDate, endDate } = singleTimelineData;
  const isReminderApplicable = REVIEW_RESPONSE_PROVIDER_TYPE_WITH_REMINDER.includes(reviewStage);

  const startDateObj = useMemo(() => (startDate ? new Date(parseISO(startDate)) : null), [startDate]);
  const endDateObj = useMemo(() => (endDate ? new Date(parseISO(endDate)) : null), [endDate]);

  const dispatch = useDispatch();

  const handleOpenTimelinePanel = () => {
    dispatch(setReviewCreationKey({ isTimelineRemindersPanelOpen: true }));
  };

  const dateRange = useMemo(() => ({ startDate: startDateObj, endDate: endDateObj }), [startDateObj, endDateObj]);

  return (
    <>
      <Flex
        alignItems="center"
        cursor="pointer"
        py="8px"
        style={{ borderBottom: '1px dashed #999999' }}
        w="max-content"
        justifyContent="space-between"
        sx={{ cursor: 'pointer' }}
        onClick={handleOpenTimelinePanel}
      >
        <TextBodyMedium color={isEmpty(dateRange) || isEmptyDateRangeValues(dateRange) ? 'text.g3' : 'text.g1'}>
          {isEmpty(dateRange) || isEmptyDateRangeValues(dateRange)
            ? 'Select duration'
            : formattedDateRangeShort(dateRange)}
        </TextBodyMedium>
      </Flex>

      {isReminderApplicable && (
        <SingleTypeReminders
          singleTypeReminderData={singleTypeReminderData}
          handleOpenTimelinePanel={handleOpenTimelinePanel}
        />
      )}
    </>
  );
};

const OneTimeTimeline = () => {
  const { timelines, reminders } = useReviewCreationV1Data();

  const applicableSections = ALL_SECTIONS_IN_ORDER.filter((section) => timelines[section]);

  const timelineData = useMemo(
    () =>
      applicableSections.map((section, index) => ({
        reviewStage: section,
        detailsComp: (
          <SingleTimelineReminder
            singleTypeReminderData={reminders?.[section] ?? []}
            singleTimelineData={timelines[section]}
          />
        ),
      })),
    [applicableSections, reminders, timelines]
  );

  if (timelineData.length === 0) return null;

  return <ReviewCreationTimeline timelineData={timelineData} />;
};

export { OneTimeTimeline };
